import { api } from './api';

export interface CreditBalance {
  subscriptionCredits: number;
  topupCredits: number;
  reserved: number;
  available: number;
  subscriptionExpireAt?: string;
  monthlyAllocation?: number;
}

export interface LedgerEntry {
  eventId: string;
  timestamp: string;
  eventType: string;
  amount: number;
  subDelta: number;
  topupDelta: number;
  reservedDelta: number;
  resourceId?: string;
  brandId?: string;
  /** Human-readable French label, set on every row going forward. */
  motif?: string;
  /** Available-balance snapshot after this row. Absent on legacy rows. */
  balanceAfter?: number;
  /** Per-line discriminator on multi-line reservations ('audit' | 'competitor'). */
  lineKey?: string;
  reason?: string;
  source?: string;
  settledAt?: string;
  metadata?: Record<string, unknown>;
}

export interface BrandUsageRollup {
  brandId: string;
  month: string;
  credits: number;
  byType: Record<string, number>;
}

export interface CreditUsage {
  month: string;
  total: number;
  dailyBurn: number;
  byBrand: BrandUsageRollup[];
  byType: Record<string, number>;
}

export const creditsService = {
  getBalance: () => api.get<CreditBalance>('/credits/balance'),
  getUsage: (month?: string) =>
    api.get<CreditUsage>(`/credits/usage${month ? `?month=${month}` : ''}`),
  getLedger: (opts: { limit?: number; before?: string; brandId?: string } = {}) => {
    const p = new URLSearchParams();
    if (opts.limit) p.set('limit', String(opts.limit));
    if (opts.before) p.set('before', opts.before);
    if (opts.brandId) p.set('brandId', opts.brandId);
    const qs = p.toString();
    return api.get<{ entries: LedgerEntry[]; nextCursor?: string }>(`/credits/ledger${qs ? '?' + qs : ''}`);
  },
  topupCheckout: (packKey: string) =>
    api.post<{ checkoutUrl: string }>('/billing/topup-checkout', { packKey }),
  agencyCheckout: (planKey: string) =>
    api.post<{
      checkoutUrl?: string;
      swapped?: boolean;
      direction?: 'upgrade' | 'downgrade';
      effectiveAt?: string;
    }>('/billing/checkout/agency', { planKey }),
  addPack: (packKey: string) =>
    api.post<{ activePacks: Record<string, number> }>('/billing/packs/add', { packKey }),
  portalSession: (returnUrl?: string) =>
    api.post<{ url: string }>('/billing/portal-session', { returnUrl }),
};
